/* eslint-disable react/jsx-no-useless-fragment */
import { BoxComponent } from '../../../../../../base/box/Box';
import { TypographyComponent } from '../../../../../../base/typography/Typography';
import { memo } from 'react';
import { getScaleSize } from '../../../../../../utils/utils';
import { useNormalizedPixels } from '../../../../../../hooks/useResize';
import { Template } from '@zeus/shared';
import placeholderItem from '../../../../../../icons/Images/Menuboard/item-placeholder.png';

interface ItemProps {
  template: Template;
  index: number;
  scale?: number;
}

const PlaceholderItem = ({ template, scale = 1, index }: ItemProps) => {
  const { normalizePixels } = useNormalizedPixels();
  const isItemPriceEnabled = template?.itemPrice && template.itemPrice.isEnabled;
  const isDescription = template?.itemDescription?.isEnabled;
  const itemTextScale = template?.item?.textScale ?? 1;
  const fontFamily = template?.fontFamilyName;

  return (
    <>
      <BoxComponent
        key={index}
        sx={{
          backgroundColor:
            (index + 1) % 2 === 0
              ? template?.evenRowsBackgroundColor || '#F4F4F8'
              : template?.oddRowsBackgroundColor || '#FAFAFA',
          borderRadius: 0,
          textTransform: 'capitalize',
          flex: 1,
          minWidth: 0,
          height: '100%',
          display: 'flex',
          flexDirection: index % 2 === 0 ? 'row' : 'row-reverse',
          overflow: 'hidden',
          position: 'relative',
          width: '100%',
          padding: '0px 3%',
          alignItems: 'center',
        }}
      >
        <BoxComponent sx={{ width: '20%', height: '90%' }}>
          <img
            src={placeholderItem as unknown as string}
            alt={`Placeholder item ${index + 1}`}
            style={{
              width: '100%',
              height: '100%',
            }}
          />
        </BoxComponent>

        <BoxComponent
          sx={{
            width: '60%',
            display: 'flex',
            flexDirection: 'column',
            rowGap: normalizePixels(1.5, 'width'),
            justifyContent: 'center',
            padding: normalizePixels(3, 'width'),
          }}
        >
          <BoxComponent
            sx={{
              display: 'flex',
              flexDirection: index % 2 === 0 ? 'row' : 'row-reverse',
              columnGap: normalizePixels(2, 'width'),
              alignItems: 'center',
            }}
          >
            <TypographyComponent
              sx={{
                ...style.itemName,
                color: template.item.itemName.color ? template.item.itemName.color : '#242528',
                minWidth: 0,
                fontSize: getScaleSize(normalizePixels(32 * itemTextScale, 'height'), scale),
                fontFamily: fontFamily,
                fontWeight: template.item.itemName.fontWeight,
                fontStyle: template.item.itemName.fontStyle,
                letterSpacing: template.item.itemName.letterSpacing,
                lineHeight: template.item.itemName.lineHeight,
              }}
            >
              Item {index + 1}
            </TypographyComponent>
            {isItemPriceEnabled && (
              <TypographyComponent
                sx={{
                  ...style.itemPrice,
                  color: template?.item?.itemPrice?.color ? template?.item?.itemPrice?.color : '#B76557',
                  fontSize: getScaleSize(normalizePixels(32 * itemTextScale, 'height'), scale),
                  fontFamily: fontFamily,
                  fontWeight: template.item.itemPrice?.fontWeight,
                  fontStyle: template.item.itemPrice?.fontStyle,
                  letterSpacing: template.item.itemPrice?.letterSpacing,
                  lineHeight: template.item.itemPrice?.lineHeight,
                }}
              >
                $ 0.00
              </TypographyComponent>
            )}
          </BoxComponent>
          {isDescription && (
            <TypographyComponent
              sx={{
                ...style.itemDesc,
                color: template.item?.itemDescription?.color ? template.item?.itemDescription?.color : '#717171',
                fontSize: getScaleSize(normalizePixels(18 * itemTextScale, 'height'), scale),
                fontFamily: fontFamily,
                fontWeight: template.item.itemDescription?.fontWeight,
                fontStyle: template.item.itemDescription?.fontStyle,
                letterSpacing: template.item.itemDescription?.letterSpacing,
                lineHeight: template.item.itemDescription?.lineHeight,
                textAlign: index % 2 === 0 ? 'left' : 'right',
              }}
            >
              Description for item {index + 1}
            </TypographyComponent>
          )}
        </BoxComponent>
      </BoxComponent>
    </>
  );
};

export default memo(PlaceholderItem);

const style = {
  container: {
    alignSelf: 'start',
    height: 'min-content',
    overflow: 'hidden',
  },
  itemNameContainer: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  itemName: {
    fontWeight: '600',
    fontFamily: 'Manrope, sans-serif',
    '-webkit-line-clamp': '1',
    '-webkit-box-orient': 'vertical',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  itemPrice: {
    textAlign: 'center' as const,
    fontFamily: 'Manrope, sans-serif',
    lineHeight: 'normal',
    fontWeight: '600',
  },
  itemDesc: {
    fontWeight: '400',
    display: '-webkit-box',
    '-webkit-line-clamp': '2',
    '-webkit-box-orient': 'vertical',
    overflow: 'hidden',
  },
};
